import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, switchMap, from, of } from 'rxjs';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class FileStorageService {
  private baseUrl = environment.wasabiUrl;
  private apiKey = environment.wasabiApiKey;

  constructor(private http: HttpClient) {}

  /**
   * Upload a base64 image to Wasabi.
   * Returns the file_id to store in DB.
   */
  uploadBase64Image(base64: string, filename: string, folder: string, metadata?: any): Observable<string> {
    const raw = base64.replace(/^data:image\/\w+;base64,/, '');
    const byteString = atob(raw);
    const bytes = new Uint8Array(byteString.length);
    for (let i = 0; i < byteString.length; i++) bytes[i] = byteString.charCodeAt(i);
    const blob = new Blob([bytes], { type: 'image/png' });

    return from(this.hashBlob(blob)).pipe(
      switchMap(hash => this.generateUrl(filename, 'image/png', folder, hash, metadata).pipe(
        switchMap(resp => {
          if (resp.alreadyExists) return of(resp.file_id);
          return from(fetch(resp.url, {
            method: 'PUT',
            headers: { 'Content-Type': 'image/png', 'x-amz-meta-file-id': resp.file_id },
            body: blob
          })).pipe(
            switchMap(() => this.confirmUpload(resp.file_id, blob.size, hash)),
            switchMap(() => of(resp.file_id))
          );
        })
      ))
    );
  }

  /** Get a presigned download URL for a file */
  getDownloadUrl(fileId: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/files/download/${fileId}`, {
      headers: { 'x-api-key': this.apiKey }
    });
  }

  private generateUrl(filename: string, filetype: string, folder: string, hash: string, metadata?: any): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/files/generate/url`, {
      filename, filetype, folder, hash, metadata
    }, { headers: { 'x-api-key': this.apiKey } });
  }

  private confirmUpload(fileId: string, size: number, hash: string): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/files/confirm/upload`, {
      file_id: fileId, size, hash
    }, { headers: { 'x-api-key': this.apiKey } });
  }

  private async hashBlob(blob: Blob): Promise<string> {
    const buffer = await blob.arrayBuffer();
    const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
    return Array.from(new Uint8Array(hashBuffer)).map(b => b.toString(16).padStart(2, '0')).join('');
  }
}
